import { EdgeMarkerType, Node, XYPosition } from 'reactflow';
import { validate as isUuid } from 'uuid';
import {
  isTranslated,
  ImageItem,
  ComponentSize,
  ICSFormatItem,
} from '@evoach/ui-components';
import { AssignAction } from 'xstate';
import { IntlShape } from 'react-intl';

import {
  ExternalServiceEnum,
  PromptEnum,
} from '../../entities/ExternalServicesTypes';

import {
  NodeDataProps,
  polarChartStateEntry,
  radarChartStateEntry,
  noteStateEntry,
  polarChartInputStateEntry,
  radioButtonStateEntry,
  multipleInputStateEntry,
  editableMultiselectStateEntry,
  needsInputStateEntry,
  emotionsInputStateEntry,
  multiButtonStateEntry,
  loopStartStateEntry,
  loopEndStateEntry,
  variableDefineStringArrayNodeStateEntry,
  EmotionProps,
  multipleInputSingleStateEntry,
  selectionCardStateEntry,
  scaleInputStateEntry,
  scaleInputMultiStateEntry,
  randomCoachMessageStateEntry,
  randomCoachMessageSingleStateEntry,
  hotOrNotSelectorStateEntry,
  startNewSessionStateEntry,
  multipleOutputCompositeStateEntry,
  multiplePercentageScaleInputStateEntry,
  compareMultipleNumbersStateEntry,
  audioDisplayStateEntry,
  imageDisplayStateEntry,
  videoDisplayStateEntry,
  setAvatarImageStateEntry,
  imageSelectorStateEntry,
  NavigateTargetEnum,
  downloadButtonStateEntry,
  createCertificateStateEntry,
  singleAnswerPromptStateEntry,
  freeTextPromptStateEntry,
  aiGenericClassificationStateEntry,
} from '.';

export const isPolarChart = (nodeData: NodeDataProps) =>
  isPolarChartEntry(nodeData.state ? nodeData.state.entry[0] : undefined);

export const isNote = (node: StateNode) =>
  node && node.type === noteStateEntry.type;

export const isPolarChartEntry = (stateEntry: StateEntryProps | undefined) =>
  (stateEntry && stateEntry.type === polarChartStateEntry.type) ||
  (stateEntry && stateEntry.type === polarChartInputStateEntry.type);
export const isRadarChartEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === radarChartStateEntry.type;
export const isRadioButtonEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === radioButtonStateEntry.type;
export const isEditableMultiselect = (
  stateEntry: StateEntryProps | undefined
) => stateEntry && stateEntry.type === editableMultiselectStateEntry.type;
export const isScaleInputEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === scaleInputStateEntry.type;

export const isAiMicrochatEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.nodeType === 'aiMicrochatStateEntry';

export const isAiListPromptEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.nodeType === 'ailistPromptStateEntry';

export const isSingleAnswerPromptEntry = (
  stateEntry: StateEntryProps | undefined
) =>
  stateEntry &&
  stateEntry.type === singleAnswerPromptStateEntry.type &&
  stateEntry.nodeType === 'singleAnswerPromptStateEntry';

export const isAiDocumentChatEntry = (
  stateEntry: StateEntryProps | undefined
) => stateEntry && stateEntry.nodeType === 'aiDocumentChatStateEntry';

export const isFreeTextPromptEntry = (
  stateEntry: StateEntryProps | undefined
) =>
  stateEntry &&
  stateEntry.type === freeTextPromptStateEntry.type &&
  stateEntry.nodeType === 'freeTextPromptStateEntry';
export const isScaleInputMultiEntry = (
  stateEntry: StateEntryProps | undefined
) => stateEntry && stateEntry.type === scaleInputMultiStateEntry.type;
export const isMultiButtonEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === multiButtonStateEntry.type;
export const isSelectionCardEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === selectionCardStateEntry.type;
//
export const isAiGenericClassificationEntry = (
  stateEntry: StateEntryProps | undefined
) => stateEntry && stateEntry.type === aiGenericClassificationStateEntry.type;
//
export const isImageSelectorEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === imageSelectorStateEntry.type;
export const isRandomCoachMessageEntry = (
  stateEntry: StateEntryProps | undefined
) =>
  stateEntry &&
  (stateEntry.type === randomCoachMessageStateEntry.type ||
    stateEntry.type === randomCoachMessageSingleStateEntry.type);
export const isMultipleInputEntry = (stateEntry: StateEntryProps | undefined) =>
  (stateEntry && stateEntry.type === multipleInputStateEntry.type) ||
  (stateEntry && stateEntry.type === multipleInputSingleStateEntry.type);
export const isEditableMultiEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry &&
  (stateEntry.type === editableMultiselectStateEntry.type ||
    stateEntry.type === needsInputStateEntry.type ||
    stateEntry.type === emotionsInputStateEntry.type);

export const isEmotionInputEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === emotionsInputStateEntry.type;

export const isNeedsInputEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry &&
  (stateEntry.type === needsInputStateEntry.type ||
    stateEntry.nodeType === 'needsInputStateEntry');

export const isHotOrNotEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === hotOrNotSelectorStateEntry.type;

export const isStartNewSessionEntry = (
  stateEntry: StateEntryProps | undefined
) => stateEntry && stateEntry.type === startNewSessionStateEntry.type;

export const isCompareMultipleNumbersStateEntry = (
  stateEntry: StateEntryProps | undefined
) => stateEntry && stateEntry.type === compareMultipleNumbersStateEntry.type;

export const isMultiplePercentageScaleEntry = (
  stateEntry: StateEntryProps | undefined
) =>
  stateEntry && stateEntry.type === multiplePercentageScaleInputStateEntry.type;

export const isAssetStateEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry &&
  (stateEntry.type === audioDisplayStateEntry.type ||
    stateEntry.type === imageDisplayStateEntry.type ||
    stateEntry.type === videoDisplayStateEntry.type ||
    stateEntry.type === downloadButtonStateEntry.type ||
    stateEntry.type === setAvatarImageStateEntry.type);

export const isCreateCertificateStateEntry = (
  stateEntry: StateEntryProps | undefined
) => stateEntry && stateEntry.type === createCertificateStateEntry.type;

export const isVariableDefineStringArrayNodeStateEntry = (
  stateEntry: StateEntryProps | undefined
) =>
  stateEntry &&
  stateEntry.type === variableDefineStringArrayNodeStateEntry.type;

export const isSaveResultToEntry = (stateEntry: StateEntryProps) =>
  Object.keys(stateEntry.payload).find(isSaveResultToKey) !== undefined;

export const isMultipleOutputEntry = (
  stateEntry: StateEntryProps | undefined
) => stateEntry && stateEntry.type === multipleOutputCompositeStateEntry.type;

// ! loop related helper function
export const isLoopStartEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === loopStartStateEntry.type;

export const isLoopEndEntry = (stateEntry: StateEntryProps | undefined) =>
  stateEntry && stateEntry.type === loopEndStateEntry.type;

export const isLoopStart = (nodeData: NodeDataProps) =>
  nodeData !== undefined && nodeData.nodeType === 'loopStartStateEntry';

export const isLoopEnd = (nodeData: NodeDataProps) =>
  nodeData !== undefined && nodeData.nodeType === 'loopEndStateEntry';

/** checks whether the variable name is generated or user-defined
 * @param {string} variablename content of saveResultTo entry in payload
 * @return {boolean} true = is user defined, false = is auto-generated
 */
export const isValidVariableName = (
  variablename: string | undefined | null
): boolean =>
  !(variablename === undefined || variablename === null) &&
  !variablename.includes('evoachechokey') &&
  !isUuid(variablename);

export const isSaveResultToKey = (payloadKey: string) =>
  payloadKey === 'saveResultTo';

export const isGetValueFromKey = (payloadKey: string) =>
  [
    'getValueFrom',
    'getValuesFrom',
    'firstValueToCompare',
    'secondValueToCompare',
  ].includes(payloadKey);

export const isGetStringValueFromKey = (payloadKey: string) =>
  payloadKey === 'getStringValues';

export const isGetTopicsValuesFromKey = (payloadKey: string) =>
  payloadKey === 'getTopicsValuesFrom';

export const isVariableProp = (payloadKey: string) =>
  ['getLabelsFrom', 'getSeriesFrom', 'getValueFrom', 'getValuesFrom'].includes(
    payloadKey
  );
/** return whether the property key is translateable
 * @param {string} payloadKey = name of payload key
 * @optional {string} payloadValue for checking whether a translatable value contains a non-translated text. This may happen if the property was formerly not translatable
 * @return {boolean} true = is translateable / probably contains a translation key, false = is not a translation key or value is not translated
 */
export const isTranslatedStatePayloadProp = (
  payloadKey: string,
  payloadValue?: string
): boolean => {
  // condition 1 : has to be in list of translateable properties
  const isInPropertyList = Object.keys(new TranslatedStatePayload()).includes(
    payloadKey
  );
  // condition 2: make sure that it is not among the list of properties
  // that were not translatable once and were made translatable later
  let isOldVersion = false;
  if (payloadValue) {
    isOldVersion =
      (payloadKey === 'src' && payloadValue.toLowerCase().startsWith('http')) ||
      (payloadKey === 'audioText' && !isTranslated(payloadValue));
  }

  return isInPropertyList && !isOldVersion;
};

export class TranslatedStatePayload implements TranslatedStatePayloadProps {
  messages: string[] = [''];
  message: string = '';
  coach: string = '';
  buttonText: string = '';
  linkButtonText: string = '';
  buttonTextYes: string = '';
  buttonTextNo: string = '';
  placeholderText: string = '';
  thankName: string = '';
  headline: string = '';
  title: string = '';
  radarLabels: string[] = [''];
  phasetext: string = '';
  labels: string[] = [''];
  placeholderTexts: string[] = [''];
  radioTexts: string[] = [''];
  buttonTexts: string[] = [''];
  scaleName: string = '';
  link: string = '';
  altText: string = '';
  src: string = '';
  assetid: string = '';
  minText: string = '';
  maxText: string = '';
  audioText: string = '';
  elements: Record<string, any>[] = [{ value: '' }];
  leftTitle: string = '';
  rightTitle?: string = '';
  ownElements: string[] = [''];
  cards?: Record<string, any>[] = [{ value: '' }];
  helperText: string = '';
  tooltipFullscreenMessage: string = '';
  scaleNames?: string[] = ['']; // PROD-1393 - MultiplePercentageScaleInput
  toolTipWarningMessage?: string = ''; // PROD-1459 - Editable Multiselect
  images?: ImageItem[] = [
    { src: '', assetid: ' ', type: 'imageselect', value: '', resolvedsrc: '' },
  ];
  prompt?: string = ''; // used for OpenAI (and similar) services
}

interface TranslatedStatePayloadProps {
  messages?: string[];
  message?: string;
  coach?: string;
  buttonText?: string;
  linkButtonText?: string;
  buttonTextYes?: string;
  buttonTextNo?: string;
  placeholderText?: string;
  thankName?: string;
  headline?: string;
  title?: string;
  radarLabels?: string[];
  phasetext?: string;
  placeholderTexts?: string[];
  radioTexts?: string[]; // used in RdaioButton
  buttonTexts?: string[]; // used in MultiButton
  topics?: string[];
  labels?: string[];
  scaleName?: string;
  link?: string; // in CopyLink node ==> is localized because links may differ for different languages
  altText?: string; // imageDisplay
  src?: string; // images, videos, etc may be language dependent
  assetid?: string; // images kann have a src as external URL or an assetid for internal URL
  minText?: string; // used in scale button
  maxText?: string; // used in scale button
  audioText?: string; // used in AudioDisplay
  elements?: Record<string, any>[]; // MultipleOutput + EditableMultiselect
  leftTitle?: string; // used in BinarySorter
  rightTitle?: string; // used in BinarySorter
  cards?: Record<string, any>[]; // used in HotOrNotSelector
  helperText?: string; // used in FormattedInput
  scaleNames?: string[]; // PROD-1393 - MultiplePercentageScaleInput
  toolTipWarningMessage?: string; // PROD-1459 - Editable Multiselect
  tooltipFullscreenMessage?: string;
  images?: ImageItem[];
  prompt?: string; // used for OpenAI (and similar) services
}

// all component rlevant information for the player should be playced within the payload
// others in entry (s. below)
export interface StatePayloadProps extends TranslatedStatePayloadProps {
  [index: string]:
    | number
    | string
    | undefined
    | number[]
    | string[]
    | boolean
    | object
    | object[]
    | ComponentSize
    | ICSFormatItem
    | ICSFormatItem[];

  series?: number[];
  width?: number | undefined | string; // string e.g. '100%'
  tickAmount?: number;
  progressPercent?: number;
  bubbleType?: string; // MultipleDisplay
  isEcho?: boolean; // MultipleInput + CanvasDisplay
  selectAll?: boolean; // MultipleOutput
  hideDate?: boolean; // MultipleOutput
  hideSelect?: boolean; // MultipleOutput
  disableDrag?: boolean; // MultipleOutput
  objectPropertyContainingMessage?: string; // CoacheeMessage when getting an array of objects
  minItems?: number | string;
  maxItems?: number | string;
  height?: number | undefined | string; // string e.g. '100%'
  scaleMin?: number; // used in Scale Button
  scaleMax?: number; // used in Scale Button
  scaleSize?: number; // used in Scale Input
  scale?: number; // used in Scale Input
  predefinedNeedsIndex?: number; // array index of the pre-defined list for NeedsInput
  predefinedEmotionsIndex?: number; // DEPRECATED for v2 ==> use it outside Payload! Array index of the pre-defined list for EmotionsInput
  getStringValues?: string[]; // used to define variables that are allowed to be used in strings
  inLoop?: string; // used in ModuleEditorPane to mark that element is in a loop - contains name of the containing loop
  keyTexts?: string[]; // used in MultiButton+RadioButton, but set to buttonTexts
  newTab?: boolean; // LinkButton
  blindSpots?: boolean; // BinarySorter
  arrows?: boolean; //BinarySorter
  emoticons?: boolean;
  displayInColums?: boolean;
  // variable management
  getTopicsValuesFrom?: string[]; // used in SingleDisplay
  getValueFrom?: string;
  getValuesFrom?: string[];
  saveResultTo?: string;
  loopName?: string; // used to pass name of loop from loop start to loop end node
  getLabelsFrom?: string; // used in PolarChar for auto-generation
  getSeriesFrom?: string; // used in PolarChar for auto-generation
  formatType?: string; // used in FormattedInput
  firstValueToCompare?: string;
  secondValueToCompare?: string;
  email?: string; // used for sending mails
  moduleId?: string; // used in Start New Session node
  cardsPerRow?: number; // PROD-1371 - define the number of columns when displaying a list of cards
  formula?: string; // PROD-1388
  keyNumbers?: number[]; // PROD-1406
  componentSize?: ComponentSize; //
  navigateTarget?: NavigateTargetEnum; // PROD-1677
  icsItems?: ICSFormatItem | ICSFormatItem[];
  externalService?: ExternalServiceEnum; // used for OpenAI (and similar) related states
  promptType?: PromptEnum; //  used for OpenAI (and similar) related states
  templateId?: string; // for printing templates
  classificationKeys?: string[]; // PROD-1949,
  points1?: string; // PROD-1942
  points2?: string; // PROD-1942
  points3?: string; // PROD-1942
  showAiBadge?: boolean; // PROD-1969
  maxTurns?: number | string; // Ai Microchat
  maxSentences?: number; // AI Summary
  assetids?: string[]; // AI Chat with Documents
  directedAgentMode?: boolean; // enable directed mode for sessions, allows AI to use state as chat history
  getPersonaFrom?: string; // variable that contains a string that is used as persona.
}
// ! if you add something to the list of params that shouldn't be translated, consider adding this property
// ! also in backend to serviceHelper.ts in global var notTranslateableKeys

/**
 * data types for variables in editor
 * ! if you change something here, consider adapting the corresponding type
 * in player in Module.ts !!
 */
export enum ReadWriteType {
  string = 0,
  stringArray = 1,
  number = 2,
  numberArray = 3,
  typedObject = 4, // type, value, starred?, date?
  time = 5, // UTC
  date = 6, // UTC
  characters = 7, // for scene canvas and CharacterSelector only
}

// StateEntryProps are relevant for the builder. All properties that are also relevant
// for the player should be placed within the payload!
export interface StateEntryProps {
  [index: string]:
    | number
    | string
    | undefined
    | number[]
    | string[]
    | StatePayloadProps
    | ReadWriteType
    | boolean
    | NodeDataProps
    | EmotionProps[]
    | ICSFormatItem
    | Record<string, string>;

  type: string;
  temporary?: boolean;
  payload: StatePayloadProps;
  displayName?: string;
  nodeType: string;
  handleOutCount?: number | 'dynamic'; // contains the expected number of handles; undefined = 1, "dyn" => dynamic number; used in qualityCheck.ts
  nodeMiniMapColor?: string;
  addPercentage?: boolean;
  saveResultType?: ReadWriteType | ReadWriteType[];
  getValueType?: ReadWriteType | ReadWriteType[];
  version?: string | number; // first, we used string but this is nonsense because you can compare with > < etc. - we now use number as type and keep string for backward compatibility
  predefinedEmotionsIndex?: number; // used in Emotionsinput
  predefinedListIndex?: number; // used in HotOrNotSelector ==> pls use this for future list indices, too!
  ownElements?: EmotionProps[]; // used in EmotionsInput
  useOwnElements?: boolean; // used in EMotionsInput
  handledByTemplate?: boolean; // e.g. used in loop or multipleInput to indicate that these elements are handled by templates in the backend
  /**
   * @deprecated . See Description.tsx for details
   */
  description?: string; // potential FAQ text
}

/** check whether a builder-related state enztry prop contains
 * translateable values
 * @params {string} stateEntryKey - bname of the key to be checked (case sensitive!)
 * @returns {boolean} true = contains translateable values,. false = otherwise
 */
export const isTranslatedStateEntryProp = (stateEntryKey: string) =>
  ['ownElements'].includes(stateEntryKey);

export interface StateExitProps {
  type: string;
  payload?: StatePayloadProps;
  temporary?: boolean;
}

// see https://xstate.js.org/docs/guides/actions.html#api for side effects
export interface StateYesNoAction {
  target: string;
  actions: string[];
}

//writeToContext: { actions: string },
export interface StateOnProps {
  [k: string]: string | undefined | StateYesNoAction | any;
  writeToContext?: any;
  next?: string;
  yes?: string | StateYesNoAction;
  no?: string | StateYesNoAction;
  good?: string;
  very_good?: string;
  bad?: string;
  very_bad?: string;
  neutral?: string; // using in Rating Input
  rating1?: string;
  rating2?: string;
  rating3?: string;
  rating4?: string;
  rating5?: string;
  setNavigation?: any; //PROD-1677
}

export interface StateAfterDelayProps {
  target: string;
}
export interface StateAfterProps {
  /**
   * waiting time in milliseconds
   */
  delay?: number;
  /**
   * id of target state
   */
  target?: string;
  actions?: string;
  /**
   * alternative to delay: wait depending on length
   */
  MESSAGE_LENGHT_BASED_DELAY?: StateAfterDelayProps;
}

// s. also xState Custome Guards
// https://xstate.js.org/docs/guides/guards.html#custom-guards
// we need guards for Loops and RandomNode
// Always props are needed for end loop states
export interface StateAlwaysProps {
  target: string;
  cond: string | Record<string, string>;
}

/**
 * This is the main interface description of a standard xState state as we use
 * it for our standard components.
 */
export interface StateProps {
  entry: StateEntryProps[];
  exit?: StateExitProps[];
  on?: StateOnProps[] | StateOnProps;
  after?: StateAfterProps[] | StateAfterProps;
  stateKey: string;
  always?: StateAlwaysProps[];
}

/**
 * This is the interface description of an "invoke" xState state as we use
 * it for components that have orechtestrated sequences of backend API calls
 * in SessionPlayer. This is required, e.g., for Start New Session node or
 * for blocking backend calls of which the player needs the result to proceed.
 *
 * The name of the src in that interface has to be defined as a "xState service"
 * when creating the machine in SessionPlayer.
 *
 * See. also these xState docs:
 * https://xstate.js.org/docs/guides/communication.html#invoking-promises
 * https://xstate.js.org/docs/patterns/sequence.html#async-sequences
 *
 */
export interface InvokeStateProps {
  invoke: {
    id: string;
    src: string; // s. section "Configuring services" in link above
    onDone: {
      target: string;
      actions?: string | AssignAction<unknown, any>;
    };
    onError: {
      target: string;
      actions?: string | AssignAction<unknown, any>;
    };
  };
  stateKey: string; // same like id but added in addition for better accessibility
  entry?: []; // no used but necessary to distinguish from StateEntryProps
}

/**
 * see also StateProps interface above. This calls helps to create new States
 * and can be used for instanceof (?)
 */
export class State implements StateProps {
  stateKey: string;
  entry: StateEntryProps[];
  exit?: StateExitProps[];
  on?: StateOnProps;
  after?: StateAfterProps[] | StateAfterProps;
  always?: StateAlwaysProps[];
  invoke?: InvokeStateProps;

  constructor(stateKey: string, entry: StateEntryProps[]) {
    this.stateKey = stateKey;
    this.entry = entry;
    this.on = { next: '' };
  }
}

export class StateNode implements Node {
  id: string;
  position: XYPosition;
  type: string | undefined;
  data: NodeDataProps;

  constructor(
    id: string,
    type: string,
    position: XYPosition,
    elementState: StateProps,
    miniMapColor?: string
  ) {
    this.id = id;
    this.type = type;
    this.position = position;
    this.data = {
      state: elementState,
      nodeMiniMapColor: miniMapColor ?? '#CCCCCC',
      nodeproperties: {},
      nodeType: type,
      helperStates: [], // s. PROD-1124, needed for StartNewSession
    };
  }
}

export class StateEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null | undefined;
  targetHandle?: string | null | undefined;
  type?: string | undefined;
  markerEnd?: EdgeMarkerType | undefined;
  markerStart?: EdgeMarkerType | undefined;

  constructor(
    id: string,
    source: string,
    target: string,
    sourceHandle?: string | null | undefined,
    targetHandle?: string | null | undefined,
    type?: string | undefined
  ) {
    this.id = id;
    this.source = source;
    this.target = target;
    this.sourceHandle = sourceHandle;
    this.targetHandle = targetHandle;
    this.type = type ?? 'buttonedge'; // new edge type implemented in base.edge.tsx and defined in ModuleEditorPane.tsx
    this.markerEnd = 'arrowclosed';
    this.markerStart = undefined;
  }
}

// ----------------------------------------------
// xState statemachine  definition

export interface StateMachineDefinitionProps {
  id: string;
  initial: string;
  context: any;
  states: { [k: string]: State | InvokeStateProps };
}

export const NodeKeyPrefix: string = 'smnode';

export interface StateMachineProps {
  id: number;
  definition: StateMachineDefinitionProps;
}
export class StateMachine implements StateMachineProps {
  id: number;
  definition: StateMachineDefinitionProps;

  constructor() {
    this.id = 1;
    const smdefinition: StateMachineDefinitionProps = {
      id: 'sm1',
      initial: NodeKeyPrefix + '1',
      context: {
        none: null,
      },
      states: {},
    };
    this.definition = smdefinition;
  }
}

export const percentageEntryTemplate: StateEntryProps = {
  type: 'setProgressPercent',
  payload: {
    progressPercent: 0,
  },
  displayName: 'Percentage',
  nodeType: 'setProgressPercentEntry',
};

/**
 * isPayloadTranslated checks every key of a payload in these steps
 * - if payload key is not translateable, it is ignored
 * - if payload key is translateable:
 *    - check for typeof = string
 *    - check for array of strings
 *    - check for array of objects
 *
 * @param {Record<string,any>} payload - payload to be checked
 * @param {IntlShape} intl - React Intl context to check messages
 * @returns {boolean} true, if everything is translated, false else
 */
export const isPayloadTranslated = (
  payload: Record<string, any>,
  intl: IntlShape
): boolean => {
  // check whether anything is untranslated and NOT it

  const filteredPayloadKeys = Object.keys(payload).filter((key: string) =>
    isTranslatedStatePayloadProp(key)
  );

  if (filteredPayloadKeys.length === 0) {
    return true;
  }

  const notEverythingTranslated = filteredPayloadKeys.some((payloadKey) => {
    // check whether payload key is translated
    if (Array.isArray(payload[payloadKey])) {
      // prop is array of strings
      if (typeof payload[payloadKey][0] === 'string') {
        return payload[payloadKey].some(
          (arrayKey: string) =>
            !stringIsNotTranslated(intl.messages[arrayKey] + '')
        );
      } else {
        // prop is array of objects
        return payload[payloadKey].some(
          (arrayKey: any) =>
            !stringIsNotTranslated(
              intl.messages[payload[payloadKey][arrayKey.value]] + ''
            )
        );
      }
    } else {
      // prop is string
      return !stringIsNotTranslated(intl.messages[payload[payloadKey]] + '');
    }
  });
  return notEverythingTranslated;
};

/**
 * stringIsTranslated checks whether string is undefined or empty or has content
 * 'Noch nicht übersetzt.' or 'Not yet translated.'
 *
 * @param {string | undefined} message
 * @returns {boolean} true, if text is translated, false else
 */
export const stringIsNotTranslated = (message: string | undefined): boolean => {
  return (
    message === undefined ||
    message === '' ||
    message === 'undefined' ||
    message === 'Noch nicht übersetzt.' ||
    message === 'Not yet translated.'
  );
};
